import type React from "react"
import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { useAppDispatch, useAppSelector } from "@/app/hooks"
import {
  fetchSitesByIds,
  selectBookmarkedSites,
  selectBookmarkedSitesStatus,
} from "@/features/maps/mapsSlice"
import { Bookmark, MapPin } from "lucide-react"
import { Tag } from "@/components/ui"
import Skeleton from "react-loading-skeleton"
import "react-loading-skeleton/dist/skeleton.css"
import "@/styles/BookmarksPage.css"

const BookmarksPage: React.FC = () => {
  const dispatch = useAppDispatch()
  const bookmarkedSites = useAppSelector(selectBookmarkedSites)
  const status = useAppSelector(selectBookmarkedSitesStatus)
  const [bookmarkIds, setBookmarkIds] = useState<string[]>([])

  useEffect(() => {
    const ids = JSON.parse(localStorage.getItem("bookmarks") || "[]")
    setBookmarkIds(ids)
    if (ids.length > 0) {
      void dispatch(fetchSitesByIds(ids))
    }
  }, [dispatch])

  if (status === "loading" && bookmarkedSites.length === 0) {
    return (
      <div className="bookmarks-page">
        <div className="bookmarks-container">
          <h1 className="page-title">북마크</h1>
          <div className="bookmarks-list">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bookmark-item">
                <Skeleton width={80} height={80} />
                <div className="bookmark-content">
                  <Skeleton width="60%" height={20} />
                  <Skeleton width="80%" height={16} />
                  <div style={{ display: 'flex', gap: '4px', marginTop: '8px' }}>
                    <Skeleton width={40} height={20} />
                    <Skeleton width={40} height={20} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (bookmarkIds.length === 0) {
    return (
      <div className="bookmarks-page">
        <div className="bookmarks-container">
          <div className="empty-state">
            <Bookmark size={64} className="empty-icon" />
            <h2>북마크가 없습니다</h2>
            <p>마음에 드는 장소를 북마크하여 저장해보세요.</p>
            <Link to="/map" style={{ marginTop: 'var(--space-6)', color: 'var(--color-primary-600)', fontWeight: 'bold', textDecoration: 'none' }}>
              지도에서 장소 찾아보기
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bookmarks-page">
      <div className="bookmarks-container">
        <h1 className="page-title">북마크</h1>
        <p className="page-description">저장한 캠핑장 목록입니다.</p>
        
        <div className="bookmarks-list">
          {bookmarkedSites.map((site) => (
            <Link key={site.id} to={`/?siteId=${site.id}`} className="bookmark-item">
              {site.imageUrls && site.imageUrls.length > 0 ? (
                <img src={site.imageUrls[0]} alt={site.name} className="bookmark-thumbnail" />
              ) : (
                <div className="bookmark-thumbnail" style={{ display: 'flex', alignItems: 'center', justifyItems: 'center', backgroundColor: 'var(--color-gray-100)' }}>
                  <MapPin size={24} style={{ margin: 'auto', color: 'var(--color-gray-300)' }} />
                </div>
              )}
              <div className="bookmark-content">
                <h3>{site.name}</h3>
                <p className="bookmark-address">{site.address}</p>
                <div className="bookmark-tags">
                  {site.tags.slice(0, 3).map((tag) => (
                    <Tag key={tag.id} variant="default" size="sm">
                      {tag.name}
                    </Tag>
                  ))}
                  {site.tags.length > 3 && (
                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                      +{site.tags.length - 3}
                    </span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  )
}

export default BookmarksPage
